import { h } from '@stencil/core';
import type { ObservableMap } from '@stencil/store';

import type { MatchStoreState } from '../../../../../services/interfaces';
import { translateEntity } from '../../../../../services/utils';
import { getMatchWording } from '../../helpers';
import {
  casesPrintPhase,
  isLiveMatch,
  isLiveMatchPenalties,
  isSizePlus,
  setCssVariant,
} from '../../helpers/match-unit.helpers';

type Props = {
  state: ObservableMap<MatchStoreState>['state'];
  onCtaClick: (e: MouseEvent) => void;
};

const getLabel = (isPlusSize: boolean, hasLiveStream: boolean) => {
  if (!isPlusSize) return 'see_more';

  return hasLiveStream ? 'watch' : 'view_details';
};

const MatchCta = ({ state, onCtaClick }: Props) => {
  const size = state.size;
  const cssVariant = setCssVariant(size);
  const match = state.matchData;
  const language = state.language;

  const isPlusSize = isSizePlus(size);

  const hasLiveStream = !!match.streamUrl?.[language];
  const showIcon = isPlusSize && hasLiveStream;
  const label = getMatchWording(getLabel(isPlusSize, hasLiveStream));

  return (
    <button
      type="button"
      class={{
        'pk-match__cta': true,
        'pk-match__cta--bg': isPlusSize,
        'pk-match__cta--with-icon': showIcon,
        ...cssVariant,
      }}
      onClick={onCtaClick}
      onAuxClick={onCtaClick}
      part="cta"
      aria-label={state.ctaAriaLabel}
    >
      {showIcon && <pk-icon name="av-play" size={16} color="interaction" title={label} />}
      {label}
    </button>
  );
};

const MatchCtaLive = ({ state, onCtaClick }: Props) => {
  const size = state.size;
  const cssVariant = setCssVariant(size);

  const label = getMatchWording('watch_live');

  return (
    <button
      type="button"
      class={{
        'pk-match__cta': true,
        'pk-match__cta--bg': true,
        'pk-match__cta--live': true,
        'pk-match__cta--with-icon': true,
        ...cssVariant,
      }}
      onClick={onCtaClick}
      onAuxClick={onCtaClick}
      part="cta"
      aria-label={state.ctaAriaLabel}
    >
      <pk-icon name="av-play" size={16} color="support-04A" title={label} />
      {label}
    </button>
  );
};

const LiveIndicator = ({ state, onCtaClick }: Props) => {
  const size = state.size;
  const cssVariant = setCssVariant(size);
  const match = state.matchData;
  const language = state.language;
  const goalScored = state.goalScored;

  const isLive = isLiveMatch(match.status);
  const isLivePenalties = isLiveMatchPenalties(match.status, match.phase);
  const hasLiveStream = !!match.streamUrl?.[language];

  if (!isLive) return null;

  if (isLivePenalties) {
    return (
      <pk-live-indicator class={{ 'mu__pk-live-indicator': true, ...cssVariant }} variant="filled" size="S">
        {translateEntity(match)?.phaseName}
      </pk-live-indicator>
    );
  }

  if (casesPrintPhase.includes(match.phase)) return null;

  return (
    <div class={{ 'mu__live-indicator': true, 'mu__live-indicator--has-goal': goalScored, ...cssVariant }}>
      <div class={{ 'mu__goal-scored': true, ...cssVariant }}>{getMatchWording('mu_goal')}</div>
      {hasLiveStream && <MatchCtaLive state={state} onCtaClick={onCtaClick} />}
      {!hasLiveStream && (
        <pk-live-indicator class={{ 'mu__pk-live-indicator': true, ...cssVariant }} variant="filled" size="S">
          {getMatchWording(match?.status.toLowerCase())}
        </pk-live-indicator>
      )}
    </div>
  );
};

export { MatchCta, MatchCtaLive, LiveIndicator };
